/**
 * La trousse 3D dans un écran React. Le moteur (three.js) n'est chargé qu'ici, à la demande : tout
 * écran qui n'affiche pas de trousse 3D n'en paie rien.
 *
 * Elle ne s'affiche que là où elle a un sens : un appareil qui sait faire du WebGL, et un visiteur
 * qui n'a pas demandé à couper les animations — dans tous les autres cas (et si le fichier ne se
 * charge pas), on garde le rendu 2D passé en `children`, qui reste la référence du catalogue.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '@jeraisonne/ui';
import { importWithRetry } from '@/lib/chunk-reload';
import type { TrousseLookSpec, TrousseScene } from './scene';
import type { TrousseShape } from './shapes';
import { loadTrousseLooks, patronUrl, type TrousseLook } from './looks';

/** L'appareil sait-il faire du WebGL ? (jsdom et les très vieux navigateurs : non.) */
export function canRender3d(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch {
    return false;
  }
}

const loadScene = () => importWithRetry(() => import('./scene'));

/** Prépare le moteur sans rien afficher (au survol d'un bouton, par exemple). */
export function preloadTrousse3d(): void {
  void loadScene().catch(() => {});
}

export function Trousse3D({
  slug,
  image,
  shape,
  patron,
  open = false,
  spin = true,
  bumpKey,
  className,
  children,
}: {
  slug: string;
  /** URL de la couverture 2D : la 3D prend exactement le même tissu */
  image?: string;
  /** forme imposée (aperçu admin) ; sinon celle enregistrée, sinon le tirage de `shapes.ts` */
  shape?: TrousseShape;
  /** URL du patron peint ; `null` = aucun (aperçu admin) ; absent = celui enregistré */
  patron?: string | null;
  open?: boolean;
  spin?: boolean;
  /** change de valeur pour faire rebondir la trousse (gagnée, échangée) */
  bumpKey?: string | number;
  className?: string;
  /** le rendu 2D, gardé tant que la 3D n'est pas là */
  children?: ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const scene = useRef<TrousseScene | null>(null);
  const [ready, setReady] = useState(false);
  const reduced = usePrefersReducedMotion();

  // l'apparence enregistrée par l'admin (forme, patron) : lue une fois par session
  const [looks, setLooks] = useState<Map<string, TrousseLook> | null>(null);
  const needLooks = shape === undefined || patron === undefined;
  useEffect(() => {
    if (!needLooks || !canRender3d()) return;
    let dead = false;
    loadTrousseLooks()
      .then((m) => !dead && setLooks(m))
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, [needLooks]);

  const spec = useMemo<TrousseLookSpec>(() => {
    const saved = looks?.get(slug);
    const savedPatron = saved?.skinPath ? patronUrl(saved.skinPath) : undefined;
    return {
      slug,
      image,
      shape: shape ?? saved?.shape ?? undefined,
      patron: patron === undefined ? savedPatron : (patron ?? undefined),
    };
  }, [looks, slug, image, shape, patron]);
  const specRef = useRef(spec);
  specRef.current = spec;

  useEffect(() => {
    if (!canRender3d()) return;
    let dead = false;
    let made: TrousseScene | null = null;
    void loadScene()
      .then((m) => {
        if (dead || !host.current) return;
        made = m.createTrousseScene(host.current, {
          ...specRef.current,
          open,
          spin,
          reducedMotion: reduced,
        });
        scene.current = made;
        setReady(true);
      })
      .catch(() => {
        // pas de 3D : le rendu 2D reste à l'écran, rien à dire à l'utilisateur
      });
    return () => {
      dead = true;
      made?.dispose();
      scene.current = null;
      setReady(false);
    };
    // la scène se monte une fois ; les changements passent par les effets ci-dessous
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);

  useEffect(() => {
    scene.current?.setLook(spec);
  }, [spec]);
  useEffect(() => {
    scene.current?.setOpen(open);
  }, [open]);
  useEffect(() => {
    scene.current?.setSpin(spin);
  }, [spin]);
  useEffect(() => {
    if (bumpKey !== undefined) scene.current?.bump();
  }, [bumpKey]);

  return (
    <div
      className={`jr-tr3d${className ? ` ${className}` : ''}`}
      data-ready={ready ? '1' : '0'}
      data-testid="trousse-3d"
    >
      <div ref={host} className="jr-tr3d-host" aria-hidden />
      {!ready && children}
    </div>
  );
}
